import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { activeMembership, useSession } from '../../lib/store';
import { Card, Notice, PageHeading, Spinner } from '../../components/Shell';
import { SelectField, TextArea, TextField, Toggle, YesNo } from '../../components/Field';
import { Stamp } from '../../components/Stamp';
import { PetDocuments } from './PetDocuments';
import type { Pet } from '../../lib/types';

/**
 * The pet intake form, which is the paper pack's longest section.
 *
 * It is presented as a carbon-copy set: each part gets its own tinted sheet, so
 * a form that runs to dozens of fields reads as five short ones. Everything
 * saves in a single request, because a client filling this in on a phone should
 * not be able to half-save a pet.
 */

const emptyPet = (clientId: string): Pet => ({
  tenantId: '',
  petId: '',
  clientId,
  name: '',
  species: 'dog',
  medical: { fullyVaccinated: false, fleaTickCurrent: false, medicationRequired: false },
  feeding: {},
  temperament: { reactions: {} },
  aggression: { hasShownAggression: false, requiresMuzzle: 'na', hasGoodRecall: 'na' },
  assessment: { status: 'not_started' },
  specialNeeds: [],
  archived: false,
  neutered: 'na',
  treatsAllowed: true,
  pumpkinAllowed: true,
  pottyTrained: true,
});

export function PetRecord() {
  const { petId } = useParams<{ petId: string }>();
  const navigate = useNavigate();
  const session = useSession();
  const membership = activeMembership(session);
  const tenantId = session.tenant?.tenantId;
  const isNew = !petId;

  const [pet, setPet] = useState<Pet | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isNew) {
      setPet(emptyPet(membership?.clientId ?? ''));
      return;
    }
    if (!tenantId || !petId) return;

    void api<{ pet: Pet }>(`/tenants/${tenantId}/pets/${petId}`)
      .then((result) => setPet(result.pet))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'This pet could not be loaded.'))
      .finally(() => setLoading(false));
  }, [tenantId, petId, isNew, membership?.clientId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Spinner label="Opening the record" />
      </div>
    );
  }

  if (!pet || !tenantId) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Notice tone="error">{error ?? 'This pet record is not available.'}</Notice>
      </div>
    );
  }

  /** Immutably patch a nested part of the record. */
  const patch = <K extends keyof Pet>(key: K, value: Pet[K]) =>
    setPet((prev) => (prev ? { ...prev, [key]: value } : prev));

  const patchGroup = <K extends 'medical' | 'feeding' | 'temperament' | 'aggression'>(
    group: K,
    value: Partial<Pet[K]>
  ) => setPet((prev) => (prev ? { ...prev, [group]: { ...prev[group], ...value } } : prev));

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setSaved(false);

    const body = {
      clientId: pet.clientId || membership?.clientId,
      name: pet.name,
      species: pet.species,
      breed: pet.breed,
      sex: pet.sex,
      birthDate: pet.birthDate,
      ageYears: pet.ageYears,
      isSenior: pet.isSenior,
      endOfLifeRequest: pet.endOfLifeRequest,
      neutered: pet.neutered,
      crateTrained: pet.crateTrained,
      pottyTrained: pet.pottyTrained,
      tagOnCollar: pet.tagOnCollar,
      treatsAllowed: pet.treatsAllowed,
      pumpkinAllowed: pet.pumpkinAllowed,
      medical: pet.medical,
      feeding: pet.feeding,
      temperament: pet.temperament,
      aggression: pet.aggression,
    };

    void (async () => {
      try {
        const result = await api<{ pet: Pet }>(
          isNew ? `/tenants/${tenantId}/pets` : `/tenants/${tenantId}/pets/${petId}`,
          { method: isNew ? 'POST' : 'PATCH', body }
        );

        if (isNew) {
          // Straight to the saved record, where documents can be uploaded.
          navigate(`/portal/pets/${result.pet.petId}`, { replace: true });
          return;
        }
        setPet(result.pet);
        setSaved(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        if (err instanceof ApiError) {
          setFieldErrors(err.fieldErrors ?? {});
          setError(err.fieldErrors ? 'Some fields need another look.' : err.message);
        } else {
          setError('The record did not save. Try again.');
        }
      } finally {
        setSaving(false);
      }
    })();
  };

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={isNew ? 'New pet record' : 'Pet record'}
        title={pet.name || 'Add a pet'}
        lead={
          isNew
            ? 'Fill this in once. Your sitter reads it before every stay, so the detail is worth it.'
            : undefined
        }
        actions={
          !isNew && pet.specialNeeds.length > 0 ? (
            <Stamp tone="pending" flat>
              {pet.specialNeeds.length} care flag{pet.specialNeeds.length > 1 ? 's' : ''}
            </Stamp>
          ) : undefined
        }
      />

      {saved && (
        <div className="mb-8">
          <Notice tone="ok">{pet.name}&rsquo;s record is saved.</Notice>
        </div>
      )}
      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-8">
        {/* --- Sheet 1: who they are ---------------------------------------- */}
        <Card tab="Part 1 &middot; The pet" className="p-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField
              label="Name"
              value={pet.name}
              onChange={(value) => patch('name', value)}
              error={fieldErrors.name}
              required
            />
            <SelectField
              label="Species"
              value={pet.species}
              onChange={(value) => patch('species', value as Pet['species'])}
              options={[
                { value: 'dog', label: 'Dog' },
                { value: 'cat', label: 'Cat' },
                { value: 'small_critter', label: 'Small critter' },
                { value: 'bird', label: 'Bird' },
                { value: 'other', label: 'Other' },
              ]}
            />
            <TextField
              label="Breed"
              value={pet.breed ?? ''}
              onChange={(value) => patch('breed', value)}
              error={fieldErrors.breed}
            />
            <SelectField
              label="Sex"
              value={pet.sex ?? ''}
              onChange={(value) => patch('sex', (value || undefined) as Pet['sex'])}
              options={[
                { value: '', label: 'Not given' },
                { value: 'F', label: 'Female' },
                { value: 'M', label: 'Male' },
              ]}
            />
            <TextField
              label="Date of birth"
              type="date"
              value={pet.birthDate ?? ''}
              onChange={(value) => patch('birthDate', value || undefined)}
              error={fieldErrors.birthDate}
              hint="Used to work out whether puppy rates apply."
            />
            <TextField
              label="Age in years"
              type="number"
              value={pet.ageYears?.toString() ?? ''}
              onChange={(value) => patch('ageYears', value ? Number(value) : undefined)}
              error={fieldErrors.ageYears}
              hint="If you do not know the exact date of birth."
            />
          </div>

          <div className="mt-6 space-y-1">
            <YesNo
              label="Neutered or spayed"
              value={pet.neutered ?? 'na'}
              onChange={(value) => patch('neutered', value)}
            />
            <Toggle
              label="Crate trained"
              checked={pet.crateTrained ?? false}
              onChange={(value) => patch('crateTrained', value)}
            />
            <Toggle
              label="Potty trained"
              checked={pet.pottyTrained ?? false}
              onChange={(value) => patch('pottyTrained', value)}
              hint="Pets who are not attract a special care rate for overnight stays."
            />
            <Toggle
              label="Wears a tag on their collar"
              checked={pet.tagOnCollar ?? false}
              onChange={(value) => patch('tagOnCollar', value)}
            />
            <Toggle
              label="Is a senior"
              checked={pet.isSenior ?? false}
              onChange={(value) => patch('isSenior', value)}
            />
          </div>

          {pet.isSenior && (
            <div className="mt-5">
              <TextArea
                label="If the worst happens while you are away"
                value={pet.endOfLifeRequest ?? ''}
                onChange={(value) => patch('endOfLifeRequest', value)}
                rows={2}
                placeholder="Who to call, and what you would want done. Hard to write, but it spares everyone a terrible decision."
              />
            </div>
          )}
        </Card>

        {/* --- Sheet 2: health ---------------------------------------------- */}
        <Card tab="Part 2 &middot; Health" tint="yellow" className="p-6">
          <div className="space-y-1">
            <Toggle
              label="Fully vaccinated"
              checked={pet.medical.fullyVaccinated}
              onChange={(value) => patchGroup('medical', { fullyVaccinated: value })}
              hint="Upload the record below so your sitter can see the dates."
            />
            <Toggle
              label="Up to date on flea and tick treatment"
              checked={pet.medical.fleaTickCurrent}
              onChange={(value) => patchGroup('medical', { fleaTickCurrent: value })}
            />
            <Toggle
              label="Microchipped"
              checked={pet.medical.microchipped ?? false}
              onChange={(value) => patchGroup('medical', { microchipped: value })}
            />
            <Toggle
              label="Insured"
              checked={pet.medical.insured ?? false}
              onChange={(value) => patchGroup('medical', { insured: value })}
            />
          </div>

          {pet.medical.insured && (
            <div className="mt-5">
              <TextField
                label="Insurance provider"
                value={pet.medical.insuranceProvider ?? ''}
                onChange={(value) => patchGroup('medical', { insuranceProvider: value })}
              />
            </div>
          )}

          <div className="mt-6 grid gap-6">
            <TextArea
              label="Allergies and intolerances"
              value={pet.medical.allergies ?? ''}
              onChange={(value) => patchGroup('medical', { allergies: value })}
              rows={2}
              error={fieldErrors['medical.allergies']}
            />
            <TextArea
              label="Medical conditions"
              value={pet.medical.conditions ?? ''}
              onChange={(value) => patchGroup('medical', { conditions: value })}
              rows={2}
              error={fieldErrors['medical.conditions']}
              placeholder="Seizures, diabetes, arthritis, anything a sitter should watch for."
            />
          </div>

          <div className="mt-6">
            <Toggle
              label="Needs medication while in care"
              checked={pet.medical.medicationRequired}
              onChange={(value) => patchGroup('medical', { medicationRequired: value })}
              hint="Administering medication carries an additional fee, shown on your quote."
            />
            {pet.medical.medicationRequired && (
              <div className="mt-4">
                <TextArea
                  label="Medication, dose and schedule"
                  value={pet.medical.medicationDetails ?? ''}
                  onChange={(value) => patchGroup('medical', { medicationDetails: value })}
                  rows={3}
                  error={fieldErrors['medical.medicationDetails']}
                  placeholder="Apoquel 16mg, one tablet with breakfast. Bottle is labelled."
                  required
                />
              </div>
            )}
          </div>
        </Card>

        {/* --- Sheet 3: food ------------------------------------------------ */}
        <Card tab="Part 3 &middot; Food" tint="blue" className="p-6">
          <p className="mb-5 text-sm text-[color:var(--color-ink-soft)]">
            Getting the food right matters more than anything else on this form. Pre-portion meals
            into labelled bags and bring two extra days&rsquo; worth.
          </p>
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField
              label="Feeding times"
              value={pet.feeding.times ?? ''}
              onChange={(value) => patchGroup('feeding', { times: value })}
              placeholder="7:00am and 5:30pm"
            />
            <TextField
              label="How much"
              value={pet.feeding.quantity ?? ''}
              onChange={(value) => patchGroup('feeding', { quantity: value })}
              placeholder="1 cup dry, half a can wet"
            />
          </div>
          <div className="mt-6">
            <TextField
              label="Treats"
              value={pet.feeding.treats ?? ''}
              onChange={(value) => patchGroup('feeding', { treats: value })}
            />
          </div>
          <div className="mt-5 space-y-1">
            <Toggle
              label="Meals will arrive pre-portioned"
              checked={pet.feeding.mealsPrePortioned ?? false}
              onChange={(value) => patchGroup('feeding', { mealsPrePortioned: value })}
              hint="If not, a meal prep fee applies per pet, per meal."
            />
            <Toggle
              label="Eats too fast"
              checked={pet.feeding.fastEater ?? false}
              onChange={(value) => patchGroup('feeding', { fastEater: value })}
            />
            <Toggle
              label="Treats are allowed"
              checked={pet.treatsAllowed ?? true}
              onChange={(value) => patch('treatsAllowed', value)}
            />
            <Toggle
              label="Pumpkin is allowed"
              checked={pet.pumpkinAllowed ?? true}
              onChange={(value) => patch('pumpkinAllowed', value)}
              hint="Often used to settle an upset stomach."
            />
          </div>
        </Card>

        {/* --- Sheet 4: temperament ----------------------------------------- */}
        <Card tab="Part 4 &middot; Character" tint="pink" className="p-6">
          <div className="grid gap-6">
            <TextArea
              label="What they are like"
              value={pet.temperament.description ?? ''}
              onChange={(value) => patchGroup('temperament', { description: value })}
              rows={3}
            />
            <div className="grid gap-6 sm:grid-cols-2">
              <TextField
                label="Loves"
                value={pet.temperament.loves ?? ''}
                onChange={(value) => patchGroup('temperament', { loves: value })}
                placeholder="The green squeaky frog"
              />
              <TextField
                label="Hates"
                value={pet.temperament.hates ?? ''}
                onChange={(value) => patchGroup('temperament', { hates: value })}
                placeholder="The hoover, thunder"
              />
              <TextField
                label="Jumper or climber"
                value={pet.temperament.jumperOrClimber ?? ''}
                onChange={(value) => patchGroup('temperament', { jumperOrClimber: value })}
              />
              <TextField
                label="Distinguishing features"
                value={pet.temperament.distinguishingFeatures ?? ''}
                onChange={(value) => patchGroup('temperament', { distinguishingFeatures: value })}
              />
              <TextField
                label="In the car"
                value={pet.temperament.carBehavior ?? ''}
                onChange={(value) => patchGroup('temperament', { carBehavior: value })}
              />
              <TextField
                label="Sight or hearing loss"
                value={pet.temperament.sensoryImpairments ?? ''}
                onChange={(value) => patchGroup('temperament', { sensoryImpairments: value })}
              />
            </div>
            <TextArea
              label="Bedtime routine"
              value={pet.temperament.bedtimeRoutine ?? ''}
              onChange={(value) => patchGroup('temperament', { bedtimeRoutine: value })}
              rows={2}
              placeholder="Crate at 9pm with the door left open, light off."
            />
            <TextArea
              label="Left alone"
              value={pet.temperament.canBeLeftAlone ?? ''}
              onChange={(value) => patchGroup('temperament', { canBeLeftAlone: value })}
              rows={2}
              hint="Sitters do leave the house occasionally. Say what your pet can cope with."
            />

            <fieldset>
              <legend className="field-label">Reaction to</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                {(['cats', 'dogs', 'squirrels', 'birds'] as const).map((animal) => (
                  <TextField
                    key={animal}
                    label={animal}
                    value={pet.temperament.reactions?.[animal] ?? ''}
                    onChange={(value) =>
                      patchGroup('temperament', {
                        reactions: { ...pet.temperament.reactions, [animal]: value },
                      })
                    }
                  />
                ))}
              </div>
            </fieldset>
          </div>
        </Card>

        {/* --- Sheet 5: safety ---------------------------------------------- */}
        <Card tab="Part 5 &middot; Safety" className="p-6">
          <Notice tone="warn">
            Full disclosure here is a term of the services agreement. A sitter who knows about a
            trigger can plan around it; one who finds out on the day cannot.
          </Notice>

          <div className="mt-6">
            <Toggle
              label="Has shown aggression toward a person or another animal"
              checked={pet.aggression.hasShownAggression}
              onChange={(value) => patchGroup('aggression', { hasShownAggression: value })}
            />
            {pet.aggression.hasShownAggression && (
              <div className="mt-4">
                <TextArea
                  label="What happened"
                  value={pet.aggression.details ?? ''}
                  onChange={(value) => patchGroup('aggression', { details: value })}
                  rows={3}
                  error={fieldErrors['aggression.details']}
                  placeholder="When, what set it off, and what has changed since."
                  required
                />
              </div>
            )}
          </div>

          <div className="mt-6 space-y-1">
            <YesNo
              label="Needs a muzzle"
              value={pet.aggression.requiresMuzzle ?? 'na'}
              onChange={(value) => patchGroup('aggression', { requiresMuzzle: value })}
            />
            <YesNo
              label="Comes back when called"
              value={pet.aggression.hasGoodRecall ?? 'na'}
              onChange={(value) => patchGroup('aggression', { hasGoodRecall: value })}
            />
          </div>

          <div className="mt-6">
            <TextArea
              label="Other behaviour worth knowing"
              value={pet.temperament.behavioralConcerns ?? ''}
              onChange={(value) => patchGroup('temperament', { behavioralConcerns: value })}
              rows={2}
              placeholder="Guards food or toys, afraid of fireworks, escapes through gates."
            />
          </div>
        </Card>

        <div className="flex flex-wrap gap-3">
          <button type="submit" className="btn btn-primary" disabled={saving || !pet.name}>
            {saving ? 'Saving' : isNew ? 'Save this pet' : 'Save changes'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/portal')}>
            Back to my pets
          </button>
        </div>
      </form>

      {/* Documents attach to a saved record, so they appear after the first save. */}
      {!isNew && petId && (
        <div className="mt-12">
          <PetDocuments tenantId={tenantId} petId={petId} petName={pet.name} />
        </div>
      )}
    </div>
  );
}
